import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Link } from "react-router-dom";
import {
    Card,
    CardActionArea,
    CardContent,
    Box,
    Typography,
    CardHeader,
    Grid,
    Tooltip,
} from "@mui/material";

import ProgressBar from "../../../Common/ProgressBar/ProgressBar";
import { CircularProgressbar } from "react-circular-progressbar";

import ThisTheme from "../../../../themes/TMSTheme";
import tmsActions from "../../../../actions/tmsActions";
import { getMyCourses } from "../../../../reducers";
import TMSAvatar from "../TMSAvatar";

const appCode = "C-TMS";

const MyEnrolmentsCard = ({ courses }) => {
    const enrolments = !!courses
        ? courses
              .filter(({ enrolment }) => !!enrolment)
              .map(({ enrolment }) => enrolment)
        : [];

    const stats = enrolments.reduce(
        (stats, { isMandatory, status }) => {
            ++stats.total;
            ++stats[isMandatory ? "mandatory" : "optional"];
            if (status === "completed") {
                ++stats[
                    isMandatory ? "mandatoryCompleted" : "optionalCompleted"
                ];
            }
            return stats;
        },
        {
            total: 0,
            mandatory: 0,
            mandatoryCompleted: 0,
            optional: 0,
            optionalCompleted: 0,
        }
    );

    const completed = stats.mandatoryCompleted + stats.optionalCompleted;
    const percentage =
        completed > 0 ? Math.round((100 * completed) / stats.total) : 0;

    const categories = [
        {
            title: "Mandatory",
            label: `/ ${stats.mandatory}`,
            total: stats.mandatoryCompleted,
            percentage:
                stats.mandatoryCompleted > 0
                    ? (100 * stats.mandatoryCompleted) / stats.mandatory
                    : 0,
        },
        {
            title: "Optional",
            label: `/ ${stats.optional}`,
            total: stats.optionalCompleted,
            percentage:
                stats.optionalCompleted > 0
                    ? (100 * stats.optionalCompleted) / stats.optional
                    : 0,
        },
    ];

    return (
        <Card className="card">
            <CardActionArea
                component={Link}
                to={"/tms/enrolments"}
                centerRipple={true}
            >
                <CardHeader
                    disableTypography={true}
                    avatar={
                        <Tooltip title="View all enrolments" arrow>
                            <TMSAvatar type="enrolment" />
                        </Tooltip>
                    }
                    title={
                        <Typography
                            component="h2"
                            className="card-header-title"
                        >
                            Enrolments
                        </Typography>
                    }
                    subheader={
                        <Typography
                            component="span"
                            className="card-header-subtitle"
                        >
                            {stats.total} course enrolments
                        </Typography>
                    }
                />
            </CardActionArea>
            <CardContent>
                <Box className="card-content">
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6} md={6}>
                            <Box className="card-content-progress-circle">
                                <CircularProgressbar
                                    value={percentage}
                                    maxValue={100}
                                    text={`${Math.round(percentage)}%`}
                                    strokeWidth={13}
                                    styles={{
                                        path: {
                                            stroke: ThisTheme.palette.primary
                                                .main,
                                            strokeLinecap: "butt",
                                            transition:
                                                "stroke-dashoffset 0.5s ease 0s",
                                            transform: "rotate(0.25turn)",
                                            transformOrigin: "center center",
                                        },
                                        trail: {
                                            stroke: "#EEEFEF",
                                            strokeLinecap: "butt",
                                        },
                                        text: {
                                            fill: "rgb(117, 117, 117)",
                                            fontSize: "18px",
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <Box className="card-content-progress-data">
                                <p>
                                    <em>{completed}</em>
                                    <br />
                                    completed
                                </p>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                {categories &&
                                    categories.length > 0 &&
                                    categories.map((category, index) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            key={index}
                                        >
                                            <ProgressBar data={category} />
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    );
};

const MyEnrolmentsContainer = () => {
    const dispatch = useDispatch();
    const courses = useSelector((state) => getMyCourses(state));

    useEffect(() => {
        dispatch(tmsActions.getEnrolments(appCode));
    }, [dispatch]);

    return <MyEnrolmentsCard courses={courses} />;
};

export default MyEnrolmentsContainer;
